import React, { useEffect, useState } from 'react';
import { G, styles, api, fmtUsd } from '../theme.js';
import { Btn, Badge, Spinner, Modal, Field, Input, Textarea, Av, Dot, Select } from '../ui.jsx';

const STAGE_COLOR = { 'New Lead': G.cyan, 'Contacted': G.blue, 'Qualified': G.purple, 'Estimate Sent': G.yellow, 'Booked': G.green, 'In Progress': G.orange, 'Invoiced': G.pink, 'Paid': G.green, 'Lost': G.red };
const TABS = [
  { id:'overview',  label:'Overview',  icon:'🏠' },
  { id:'notes',     label:'Notes',     icon:'📝' },
  { id:'messages',  label:'Messages',  icon:'💬' },
  { id:'jobs',      label:'Jobs',      icon:'🔧' },
  { id:'estimates', label:'Estimates', icon:'📄' },
  { id:'invoices',  label:'Invoices',  icon:'💰' },
  { id:'documents', label:'Documents', icon:'📑' },
  { id:'equipment', label:'Equipment', icon:'⚙️' },
];

export function ContactProfile({ contactId, onClose, onChanged, toast, mode = 'modal' }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [smsModalOpen, setSmsModalOpen] = useState(false);
  const [docModalOpen, setDocModalOpen] = useState(false);

  const load = () => api(`/api/contact-detail/${contactId}`).then(setData).catch(e=>toast(e.message,'error')).finally(()=>setLoading(false));
  useEffect(() => { if (contactId) { setLoading(true); load(); } }, [contactId]);
  // Refresh the profile when the quick-edit drawer (or anything else) saves a
  // change to this contact. Without this, the open profile shows stale data
  // and the save *looks* broken even though the server updated successfully.
  useEffect(() => {
    if (!contactId) return;
    const onUpdated = (e) => { if (e.detail?.id === contactId) load(); };
    window.addEventListener('tradeops:contact-updated', onUpdated);
    return () => window.removeEventListener('tradeops:contact-updated', onUpdated);
  }, [contactId]);

  if (!contactId) return null;
  if (loading || !data) {
    const sp = <div style={{ padding:40, textAlign:'center' }}><Spinner sz={32}/></div>;
    return mode === 'page' ? <div style={{ padding:24 }}>{sp}</div> : <Modal open onClose={onClose}>{sp}</Modal>;
  }

  const c = data.contact;
  const sendPortalLink = async () => {
    if (!c.phone) return toast('No phone on file', 'warn');
    if (!c.portalToken) {
      // Trigger creation by hitting the contact patch
      try { await api(`/api/contacts/${c.id}`, { method:'PATCH', body:{} }); load(); }
      catch(e) { toast(e.message,'error'); }
    }
    const url = `${window.location.protocol}//${window.location.hostname}:5050/portal/customer/${c.id}?t=${c.portalToken||''}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url);
      toast('Portal URL copied to clipboard');
    } else {
      window.prompt('Portal URL:', url);
    }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    try { await api('/api/notes', { method:'POST', body:{ contactId: c.id, body: newNote }}); setNewNote(''); load(); toast('Note added'); }
    catch(e) { toast(e.message,'error'); }
  };
  const togglePin = async (n) => {
    try { await api(`/api/notes/${n.id}`, { method:'PATCH', body:{ pinned: !n.pinned }}); load(); }
    catch(e) { toast(e.message,'error'); }
  };
  const delNote = async (n) => {
    if (!confirm('Delete this note?')) return;
    try { await api(`/api/notes/${n.id}`, { method:'DELETE' }); load(); }
    catch(e) { toast(e.message,'error'); }
  };

  const Wrap = mode === 'page'
    ? ({ children }) => <div style={{ maxWidth: 1100, margin: '0 auto', padding: 24 }}>{children}</div>
    : ({ children }) => <Modal open onClose={onClose} width={900}>{children}</Modal>;

  return <Wrap>
    {/* Header */}
    <div data-stack style={{ display:'flex', alignItems:'center', gap:14, marginBottom:14 }}>
      <Av name={`${c.firstName||''} ${c.lastName||''}`} sz={56}/>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
          <h2 style={{ margin:0, fontSize:22 }}>{c.firstName} {c.lastName}</h2>
          <Badge c={STAGE_COLOR[c.stage]||G.textMuted} bg={(STAGE_COLOR[c.stage]||G.textMuted)+'22'}>{c.stage}</Badge>
          {data.summary.hasMembership && <Badge c={G.purple} bg={G.purpleDim}>⭐ Member</Badge>}
        </div>
        <div style={{ fontSize:13, color:G.textMuted, marginTop:4 }}>
          {c.phone && <span><a onClick={(e)=>{ e.preventDefault(); try { window.dispatchEvent(new CustomEvent('tradeops:voip-call', { detail:{ to:c.phone, name:`${c.firstName||''} ${c.lastName||''}`.trim() }})); } catch {} }} href={`tel:${c.phone}`} style={{ color:G.cyan, textDecoration:'none', cursor:'pointer' }}>📞 {c.phone}</a></span>}
          {c.email && <span> · <a href={`mailto:${c.email}`} style={{ color:G.cyan, textDecoration:'none' }}>📧 {c.email}</a></span>}
        </div>
        {(() => {
          const fullAddr = [c.address, c.city, c.state, c.zip].filter(Boolean).join(' ').trim();
          if (!fullAddr) return null;
          const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddr)}`;
          return <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>
            <a href={mapsUrl} target="_blank" rel="noopener noreferrer" title="Open in Google Maps"
              style={{ color:G.cyan, textDecoration:'none', cursor:'pointer' }}>
              🗺️ {c.address}{c.city ? `, ${c.city}` : ''} {c.state} {c.zip}
            </a>
          </div>;
        })()}
        <div style={{ fontSize:11, color:G.textMuted, marginTop:6, display:'flex', gap:14, flexWrap:'wrap' }}>
          <span>🗓️ <strong style={{ color:G.text }}>Created:</strong> {c.createdAt ? new Date(c.createdAt).toLocaleString() : '—'}</span>
          <span>📞 <strong style={{ color:G.text }}>Last Contacted:</strong> {c.lastContact ? new Date(c.lastContact).toLocaleString() : 'Never'}</span>
        </div>
        <WorkflowEnrollmentStrip contactId={c.id} toast={toast}/>
      </div>
      <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
        <Btn sz="sm" onClick={() => {
          // Drawer renders on top via z-index 5001 — no need to close profile first.
          try { window.dispatchEvent(new CustomEvent('tradeops:edit-contact', { detail: { id: contactId }})); } catch {}
        }}>✏️ Edit</Btn>
        {c.phone && <Btn sz="sm" v="cyan" onClick={()=>setSmsModalOpen(true)}>💬 Send SMS</Btn>}
        {c.phone && <Btn sz="sm" v="green" onClick={() => {
          try { window.dispatchEvent(new CustomEvent('tradeops:voip-call', { detail:{ to:c.phone, name:`${c.firstName||''} ${c.lastName||''}`.trim() }})); } catch {}
        }}>📞 Call</Btn>}
        {c.phone && <Btn sz="sm" v="purple" onClick={async()=>{
          if (!confirm(`Have the AI agent call ${c.firstName} ${c.lastName || ''} at ${c.phone}?`)) return;
          try {
            const r = await api('/api/agents/outbound-call', { method:'POST', body:{ contactId: c.id, scriptKey: 'new_lead' } });
            toast(r.callId ? 'AI is calling now — transcript will land in the comm log' : 'Call started', 'success');
          } catch(e) { toast(e.message, 'error'); }
        }}>🤖 Call with AI</Btn>}
        <Btn sz="sm" onClick={()=>setDocModalOpen(true)}>📎 Upload Doc</Btn>
        <Btn sz="sm" onClick={sendPortalLink}>📲 Portal Link</Btn>
        <ContactQuickActions contact={c} onChanged={load} toast={toast}/>
      </div>
    </div>

    {/* KPI strip */}
    <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:8, marginBottom:14 }}>
      <Mini label="Lifetime Value" value={fmtUsd(data.summary.lifetimeValue)} color={G.green}/>
      <Mini label="Jobs Done" value={data.summary.jobsCompleted} color={G.cyan}/>
      <Mini label="Active" value={data.summary.jobsActive} color={G.purple}/>
      <Mini label="Open Invoices" value={data.summary.openInvoices} color={G.orange}/>
      <Mini label="Pending Estimates" value={data.summary.pendingEstimates} color={G.yellow}/>
    </div>

    {/* Tabs — primary visible, rarely-used items behind "More ▾" */}
    <ContactTabBar tab={tab} setTab={setTab} data={data}/>

    <SendSmsModal open={smsModalOpen} contact={c} onClose={()=>setSmsModalOpen(false)} onSent={()=>{ setSmsModalOpen(false); load(); toast('SMS sent'); }} toast={toast}/>
    <UploadDocModal open={docModalOpen} contact={c} onClose={()=>setDocModalOpen(false)} onUploaded={()=>{ setDocModalOpen(false); load(); toast('Document attached'); }} toast={toast}/>

    {/* Tab body */}
    <div style={{ minHeight:300 }}>
      {tab === 'overview' && <Overview data={data}/>}
      {tab === 'notes' && <Notes notes={data.notes} newNote={newNote} setNewNote={setNewNote} onAdd={addNote} onPin={togglePin} onDelete={delNote}/>}
      {tab === 'jobs' && <JobsList jobs={data.jobs}/>}
      {tab === 'estimates' && <EstimatesList estimates={data.estimates}/>}
      {tab === 'invoices' && <InvoicesList invoices={data.invoices}/>}
      {tab === 'documents' && <DocumentsList documents={data.documents}/>}
      {tab === 'equipment' && <EquipmentList equipment={data.equipment}/>}
      {tab === 'messages' && <UnifiedCommLog contactId={c.id}/>}
    </div>
  </Wrap>;
}

function Mini({ label, value, color }) {
  return <div style={{ ...styles.card, padding:10 }}>
    <div style={{ fontSize:10, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em' }}>{label}</div>
    <div style={{ fontSize:18, fontWeight:800, color, marginTop:2 }}>{value}</div>
  </div>;
}

const TIMELINE_ICON = { sms:'💬', email:'✉️', call:'📞', note:'📝', pipeline:'🎯', edit:'✏️', job:'🔧', invoice:'💰', review:'⭐' };
function WorkflowEnrollmentStrip({ contactId, toast }) {
  const [enrollments, setEnrollments] = useState(null);
  const [workflows, setWorkflows] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  const load = () => api(`/api/workflows/contact/${contactId}/enrollments`).then(r => setEnrollments(r.enrollments||[])).catch(()=>setEnrollments([]));
  useEffect(() => { load(); }, [contactId]);
  useEffect(() => {
    if (!pickerOpen || workflows.length) return;
    api('/api/workflows').then(r => setWorkflows((r.workflows||r||[]).filter(w => w.status === 'active'))).catch(()=>setWorkflows([]));
  }, [pickerOpen]);

  const remove = async (enrollmentId, workflowName) => {
    if (!confirm(`Remove this contact from "${workflowName}"?`)) return;
    try { await api(`/api/workflows/enrollments/${enrollmentId}`, { method:'DELETE' }); load(); toast && toast('Removed from workflow'); }
    catch (e) { toast && toast(e.message, 'error'); }
  };
  const enroll = async (workflowId, workflowName) => {
    try {
      await api(`/api/workflows/${workflowId}/enroll/${contactId}`, { method:'POST' });
      setPickerOpen(false); load(); toast && toast(`✓ Enrolled in "${workflowName}"`);
    } catch (e) { toast && toast(e.message, 'error'); }
  };
  if (enrollments === null) return null;
  const activeIds = new Set(enrollments.map(e => e.workflow_id));
  const candidates = workflows.filter(w => !activeIds.has(w.id));
  return <div style={{ marginTop:8, display:'flex', gap:6, flexWrap:'wrap', alignItems:'center', position:'relative' }}>
    <span style={{ fontSize:11, color:G.textMuted, fontWeight:600, textTransform:'uppercase', letterSpacing:'.04em' }}>Workflows:</span>
    {enrollments.length === 0 && <span style={{ fontSize:11, color:G.textMuted, fontStyle:'italic' }}>none</span>}
    {enrollments.map(e => (
      <span key={e.id} style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'2px 8px', background:G.purpleDim, color:G.purple, borderRadius:12, fontSize:11, fontWeight:600 }}>
        ⚡ {e.workflow_name}
        <span onClick={()=>remove(e.id, e.workflow_name)} style={{ cursor:'pointer', marginLeft:2, opacity:.6 }} title="Remove from workflow">✕</span>
      </span>
    ))}
    <Btn sz="xs" v="ghost" onClick={()=>setPickerOpen(p => !p)}>{pickerOpen ? 'Close' : '+ Add to workflow'}</Btn>
    {pickerOpen && <div style={{ position:'absolute', top:'100%', left:0, marginTop:6, zIndex:50, background:G.card, border:`1px solid ${G.cardBorder}`, borderRadius:8, boxShadow:'0 6px 18px rgba(0,0,0,.25)', minWidth:240, maxHeight:280, overflow:'auto', padding:4 }}>
      {candidates.length === 0 && <div style={{ padding:'8px 12px', fontSize:12, color:G.textMuted }}>No other active workflows.</div>}
      {candidates.map(w => (
        <div key={w.id} onClick={()=>enroll(w.id, w.name)} style={{ padding:'8px 12px', cursor:'pointer', fontSize:13, borderRadius:6 }}
          onMouseEnter={e=>e.currentTarget.style.background=G.input}
          onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
          {w.name}
        </div>
      ))}
    </div>}
  </div>;
}

function TimelineFeed({ contactId }) {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState('all');
  useEffect(() => {
    let on = true;
    api(`/api/contacts/${contactId}/timeline`).then(d => on && setItems(d.items || [])).catch(()=>on && setItems([]));
    return () => { on = false; };
  }, [contactId]);
  if (items === null) return <div style={{ padding:20 }}><Spinner sz={18}/></div>;
  const filtered = filter === 'all' ? items : items.filter(i => i.kind === filter);
  const kinds = ['all', ...new Set(items.map(i => i.kind))];
  return <div>
    <div style={{ display:'flex', gap:6, marginBottom:12, flexWrap:'wrap' }}>
      {kinds.map(k => (
        <button key={k} onClick={()=>setFilter(k)} style={{
          padding:'4px 10px', borderRadius:6, border:`1px solid ${filter===k?G.purple:G.border}`,
          background: filter===k ? G.purpleDim : 'transparent', color: G.text, fontSize:11, cursor:'pointer',
        }}>{k === 'all' ? `All (${items.length})` : `${TIMELINE_ICON[k]||''} ${k}`}</button>
      ))}
    </div>
    {filtered.length === 0 && <div style={{ color:G.textMuted, fontSize:13, padding:'8px 0' }}>Nothing yet.</div>}
    {filtered.slice(0, 80).map((a,i) => (
      <div key={i} style={{ display:'flex', gap:10, padding:'10px 0', borderBottom:`1px solid ${G.border}`, fontSize:13 }}>
        <span style={{ width:20, fontSize:16 }}>{TIMELINE_ICON[a.kind] || '•'}</span>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', gap:8, alignItems:'baseline' }}>
            <span style={{ fontWeight:600 }}>{a.title}</span>
            {a.direction === 'inbound' && <span style={{ fontSize:10, color:G.green }}>← inbound</span>}
            {a.direction === 'outbound' && <span style={{ fontSize:10, color:G.cyan }}>→ outbound</span>}
          </div>
          {a.body && <div style={{ color:G.textMuted, marginTop:2, whiteSpace:'pre-wrap', wordBreak:'break-word' }}>{a.body.length > 240 ? a.body.slice(0,240)+'…' : a.body}</div>}
        </div>
        <span style={{ fontSize:11, color:G.textMuted, whiteSpace:'nowrap' }}>{a.at ? new Date(a.at).toLocaleString([], { month:'short', day:'numeric', hour:'numeric', minute:'2-digit' }) : ''}</span>
      </div>
    ))}
  </div>;
}

function Overview({ data }) {
  const { contact: c, summary } = data;
  const [mapsKey, setMapsKey] = useState(null);
  useEffect(() => {
    // Cache per session — config is tiny, no need to refetch per profile open
    if (window.__tradeopsConfig?.googleMapsKey !== undefined) { setMapsKey(window.__tradeopsConfig.googleMapsKey); return; }
    api('/api/config/public').then(cfg => { window.__tradeopsConfig = cfg; setMapsKey(cfg.googleMapsKey); }).catch(()=>{});
  }, []);
  // Build a clean address string for the geocoder. Humans often paste the WHOLE
  // address into c.address (e.g. "1234 Main St, Tampa, FL 33601") AND have
  // city/state/zip set separately — naively joining produces dupes that confuse
  // Google. Heuristic: if c.address already contains a comma, trust it as-is.
  const fullAddr = (() => {
    const street = (c.address || '').trim();
    const looksComplete = street.includes(',') && (street.match(/\b\d{5}\b/) || /[A-Z]{2}\b/.test(street));
    if (looksComplete) return street;
    return [street, c.city, c.state, c.zip].filter(Boolean).join(', ').trim();
  })();
  const mapsUrl = fullAddr ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddr)}` : null;
  // Maps Embed API when key is configured (real geocoding); fall back to the
  // free lite embed when no key (works but may guess on ambiguous addresses).
  const embedUrl = fullAddr
    ? (mapsKey
        ? `https://www.google.com/maps/embed/v1/place?key=${mapsKey}&q=${encodeURIComponent(fullAddr)}`
        : `https://maps.google.com/maps?q=${encodeURIComponent(fullAddr)}&output=embed`)
    : null;
  return <div>
    <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:14 }}>
      <div style={styles.card}>
        <h3 style={{ margin:'0 0 12px 0', fontSize:14 }}>📊 Activity Timeline</h3>
        <TimelineFeed contactId={c.id}/>
      </div>
      <div>
        <div style={{ ...styles.card, marginBottom:10 }}>
          <h3 style={{ margin:'0 0 8px 0', fontSize:14 }}>📌 Quick Facts</h3>
          <Row k="Stage" v={c.stage}/>
          <Row k="Source" v={c.source}/>
          <Row k="Service" v={c.service||'—'}/>
          <Row k="Tags" v={(c.tags||[]).join(', ') || '—'}/>
          <Row k="Customer Since" v={new Date(c.createdAt).toLocaleDateString()}/>
          <Row k="Last Contact" v={c.lastContact ? new Date(c.lastContact).toLocaleDateString() : '—'}/>
        </div>
        {fullAddr && <div style={{ ...styles.card, padding:0, overflow:'hidden' }}>
          <div style={{ padding:'10px 14px 6px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <h3 style={{ margin:0, fontSize:14 }}>📍 Location</h3>
            <a href={mapsUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize:12, color:G.cyan, textDecoration:'none' }}>Open in Maps ↗</a>
          </div>
          <div style={{ fontSize:12, color:G.textMuted, padding:'0 14px 8px' }}>{fullAddr}</div>
          <a href={mapsUrl} target="_blank" rel="noopener noreferrer" style={{ display:'block', position:'relative' }}>
            {/* key={embedUrl} forces a fresh iframe when the address string changes
                — otherwise Chrome can re-use a cached lite-embed mistakenly */}
            <iframe
              key={embedUrl}
              src={embedUrl}
              title="Customer location"
              width="100%" height="220" frameBorder="0"
              style={{ border:0, display:'block', pointerEvents:'none' }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            <div style={{ position:'absolute', inset:0, cursor:'pointer' }} title="Click to open in Google Maps"/>
          </a>
          <div style={{ padding:'6px 14px 10px', fontSize:10, color:G.textDim, fontFamily:'monospace', wordBreak:'break-all' }}>
            Geocoding: <code>{fullAddr}</code>{mapsKey ? ' · Embed API' : ' · Lite embed (no key)'}
          </div>
        </div>}
      </div>
    </div>
  </div>;
}

function Row({ k, v }) {
  return <div style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', fontSize:13 }}>
    <span style={{ color:G.textMuted }}>{k}</span>
    <strong>{v}</strong>
  </div>;
}

// Primary tabs visible inline; secondary tabs hidden behind "More ▾".
// Closes the dropdown on outside click and when a secondary tab is picked.
function ContactTabBar({ tab, setTab, data }) {
  const [showMore, setShowMore] = useState(false);
  const counts = {
    notes: data.notes.length, jobs: data.jobs.length, estimates: data.estimates.length,
    invoices: data.invoices.length, documents: data.documents.length, equipment: data.equipment.length,
    messages: data.recentMessages.length,
  };
  const primary = TABS.filter(t => !t.secondary);
  const secondary = TABS.filter(t => t.secondary);
  const activeSecondary = secondary.find(t => t.id === tab);
  const moreLabel = activeSecondary ? `${activeSecondary.icon} ${activeSecondary.label}` : '⋯ More';
  // Close dropdown on outside click — without this, the popup gets stuck open after
  // navigating away. Listener attached only while open to avoid leak.
  useEffect(() => {
    if (!showMore) return;
    const close = (e) => {
      // Click inside the More button or dropdown? leave it alone.
      if (e.target.closest('[data-more-root]')) return;
      setShowMore(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [showMore]);
  // Split the bar in two: primary tabs scroll horizontally on overflow, but
  // the "More ▾" dropdown sits OUTSIDE the scroll container so its absolutely-
  // positioned popup isn't clipped by the parent's overflowX.
  return <div style={{ display:'flex', alignItems:'stretch', borderBottom:`1px solid ${G.border}`, marginBottom:14 }}>
    <div style={{ display:'flex', gap:4, overflowX:'auto', flex:1 }}>
      {primary.map(t => (
        <div key={t.id} onClick={()=>setTab(t.id)} style={{
          padding:'10px 14px', cursor:'pointer',
          borderBottom: tab===t.id ? `2px solid ${G.purple}` : '2px solid transparent',
          color: tab===t.id ? G.purple : G.textMuted, fontWeight:600, fontSize:13, whiteSpace:'nowrap',
        }}>
          {t.icon} {t.label}{counts[t.id] !== undefined ? ` (${counts[t.id]})` : ''}
        </div>
      ))}
    </div>
    {secondary.length > 0 && <div data-more-root style={{ position:'relative' }}>
      <div onClick={()=>setShowMore(s=>!s)} style={{
        padding:'10px 14px', cursor:'pointer',
        borderBottom: activeSecondary ? `2px solid ${G.purple}` : '2px solid transparent',
        color: activeSecondary ? G.purple : G.textMuted, fontWeight:600, fontSize:13, whiteSpace:'nowrap',
      }}>{moreLabel}</div>
      {showMore && <div style={{
        position:'absolute', top:'100%', right:0, marginTop:4, zIndex:50,
        background:G.card, border:`1px solid ${G.cardBorder}`, borderRadius:8,
        boxShadow:'0 8px 24px rgba(0,0,0,.25)', minWidth:170, padding:4,
      }}>
        {secondary.map(t => (
          <div key={t.id} onClick={()=>{ setTab(t.id); setShowMore(false); }} style={{
            padding:'8px 12px', borderRadius:6, cursor:'pointer', fontSize:13,
            color: tab===t.id ? G.purple : G.text, fontWeight: tab===t.id ? 700 : 500,
            background: tab===t.id ? G.purpleDim : 'transparent',
          }}
          onMouseEnter={e=>e.currentTarget.style.background=G.input}
          onMouseLeave={e=>e.currentTarget.style.background=tab===t.id?G.purpleDim:'transparent'}>
            {t.icon} {t.label}{counts[t.id] !== undefined ? ` (${counts[t.id]})` : ''}
          </div>
        ))}
      </div>}
    </div>}
  </div>;
}

function Notes({ notes, newNote, setNewNote, onAdd, onPin, onDelete }) {
  return <div>
    <div style={{ display:'flex', gap:8, marginBottom:14 }}>
      <Textarea value={newNote} onChange={e=>setNewNote(e.target.value)} placeholder="Add a note... (gate code, dog, customer prefs, etc.)" style={{ minHeight:60 }}/>
      <Btn v="primary" onClick={onAdd}>+ Note</Btn>
    </div>
    {notes.map(n => <NoteCard key={n.id} note={n} onPin={onPin} onDelete={onDelete}/>)}
    {notes.length === 0 && <div style={{ padding:30, textAlign:'center', color:G.textMuted, fontSize:13 }}>No notes yet. Pin important details so the whole team sees them.</div>}
  </div>;
}

// Renders a note with markdown-link parsing + inline audio player for any
// markdown link whose URL ends in a recording extension (mp3/wav/m4a).
// Notes with kind='ai_call' get a purple AI badge so owners spot them fast.
function NoteCard({ note, onPin, onDelete }) {
  const isAiCall = note.kind === 'ai_call';
  // Extract the first recording URL from the body so we can show a player at the top.
  const audioMatch = (note.body || '').match(/\[([^\]]+)\]\((https?:\/\/[^)]+\.(?:mp3|wav|m4a|ogg))\)/i);
  const audioUrl = audioMatch?.[2];

  // Split body into segments around markdown links so we can render them as <a>.
  const linkRegex = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g;
  const parts = [];
  let lastIndex = 0;
  let m;
  while ((m = linkRegex.exec(note.body || '')) !== null) {
    if (m.index > lastIndex) parts.push({ kind: 'text', text: note.body.slice(lastIndex, m.index) });
    parts.push({ kind: 'link', text: m[1], href: m[2] });
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < (note.body || '').length) parts.push({ kind: 'text', text: note.body.slice(lastIndex) });

  return (
    <div style={{ ...styles.card, padding:12, marginBottom:8,
      borderColor: isAiCall ? G.purple+'88' : note.pinned ? G.yellow+'66' : G.cardBorder,
      background: isAiCall ? G.purpleDim+'22' : note.pinned ? G.yellowLight : G.card }}>
      {isAiCall && <div style={{ fontSize:10, fontWeight:800, color:G.purple, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:6 }}>
        🤖 AI Voice Call
      </div>}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:10 }}>
        <div style={{ flex:1, minWidth:0 }}>
          {audioUrl && (
            <audio controls src={audioUrl} style={{ width:'100%', marginBottom:8, height:36 }}/>
          )}
          <div style={{ fontSize:13, lineHeight:1.5, whiteSpace:'pre-wrap' }}>
            {parts.length ? parts.map((p, i) =>
              p.kind === 'link'
                ? <a key={i} href={p.href} target="_blank" rel="noreferrer" style={{ color:G.cyan, textDecoration:'underline' }}>{p.text}</a>
                : <React.Fragment key={i}>{p.text}</React.Fragment>
            ) : (note.body || '')}
          </div>
          <div style={{ fontSize:11, color:G.textMuted, marginTop:6 }}>
            {note.authorName} · {new Date(note.createdAt).toLocaleString()}
          </div>
        </div>
        <div style={{ display:'flex', gap:4 }}>
          <Btn sz="xs" v={note.pinned?'yellow':'ghost'} onClick={()=>onPin(note)}>📌</Btn>
          <Btn sz="xs" v="danger" onClick={()=>onDelete(note)}>×</Btn>
        </div>
      </div>
    </div>
  );
}

function JobsList({ jobs }) {
  if (!jobs.length) return <Empty msg="No jobs yet."/>;
  const STAT = { Scheduled:G.blue, Booked:G.cyan, Dispatched:G.purple, 'En Route':G.cyan, 'On Site':G.orange, Completed:G.green, Cancelled:G.red };
  return <table style={{ width:'100%', borderCollapse:'collapse' }}>
    <thead><tr>{['Job','Status','Tech','Date','Value'].map(h=><th key={h} style={styles.th}>{h}</th>)}</tr></thead>
    <tbody>{jobs.map(j => (
      <tr key={j.id}>
        <td style={{ ...styles.td, fontWeight:600 }}>{j.name}</td>
        <td style={styles.td}><Badge c={STAT[j.status]||G.textMuted} bg={(STAT[j.status]||G.textMuted)+'22'}>{j.status}</Badge></td>
        <td style={styles.td}>{j.tech||'—'}</td>
        <td style={{ ...styles.td, fontSize:12 }}>{j.scheduledDate||'—'}</td>
        <td style={{ ...styles.td, fontWeight:600 }}>{fmtUsd(j.est||0)}</td>
      </tr>
    ))}</tbody>
  </table>;
}

function EstimatesList({ estimates }) {
  if (!estimates.length) return <Empty msg="No estimates yet."/>;
  return <table style={{ width:'100%', borderCollapse:'collapse' }}>
    <thead><tr>{['#','Title','Total','Status','Created'].map(h=><th key={h} style={styles.th}>{h}</th>)}</tr></thead>
    <tbody>{estimates.map(e => {
      const sub = (e.lines||[]).reduce((s,l)=>s+(Number(l.qty)||0)*(Number(l.price)||0), 0);
      const total = sub * (1 + (e.taxRate||0)/100);
      return <tr key={e.id}>
        <td style={{ ...styles.td, fontWeight:600 }}>{e.num}</td>
        <td style={styles.td}>{e.title}</td>
        <td style={{ ...styles.td, fontWeight:600 }}>{fmtUsd(total)}</td>
        <td style={styles.td}><Badge c={G.cyan} bg={G.cyanLight}>{e.status}</Badge></td>
        <td style={{ ...styles.td, fontSize:12, color:G.textMuted }}>{new Date(e.createdAt).toLocaleDateString()}</td>
      </tr>;
    })}</tbody>
  </table>;
}

function InvoicesList({ invoices }) {
  if (!invoices.length) return <Empty msg="No invoices yet."/>;
  return <table style={{ width:'100%', borderCollapse:'collapse' }}>
    <thead><tr>{['#','Service','Total','Paid','Status','Due'].map(h=><th key={h} style={styles.th}>{h}</th>)}</tr></thead>
    <tbody>{invoices.map(i => {
      const sub = (i.lines||[]).reduce((s,l)=>s+(Number(l.qty)||0)*(Number(l.price)||0), 0);
      const total = sub * (1 + (i.taxRate||0)/100);
      return <tr key={i.id}>
        <td style={{ ...styles.td, fontWeight:600 }}>{i.num}</td>
        <td style={styles.td}>{i.service||'—'}</td>
        <td style={{ ...styles.td, fontWeight:600 }}>{fmtUsd(total)}</td>
        <td style={styles.td}>{fmtUsd(i.paid||0)}</td>
        <td style={styles.td}><Badge c={i.status==='paid'?G.green:i.status==='overdue'?G.red:G.cyan} bg={(i.status==='paid'?G.green:i.status==='overdue'?G.red:G.cyan)+'22'}>{i.status}</Badge></td>
        <td style={{ ...styles.td, fontSize:12, color:G.textMuted }}>{i.due||'—'}</td>
      </tr>;
    })}</tbody>
  </table>;
}

function DocumentsList({ documents }) {
  if (!documents.length) return <Empty msg="No documents yet. Pre-job inspections and post-job sign-offs auto-create when techs advance jobs."/>;
  return <table style={{ width:'100%', borderCollapse:'collapse' }}>
    <thead><tr>{['Title','Type','Status','Signed'].map(h=><th key={h} style={styles.th}>{h}</th>)}</tr></thead>
    <tbody>{documents.map(d => (
      <tr key={d.id}>
        <td style={{ ...styles.td, fontWeight:600 }}>{d.title}</td>
        <td style={styles.td}><Badge c={G.cyan} bg={G.cyanLight}>{d.type}</Badge></td>
        <td style={styles.td}><Badge c={d.status==='signed'?G.green:G.textMuted} bg={(d.status==='signed'?G.green:G.textMuted)+'22'}>{d.status}</Badge></td>
        <td style={{ ...styles.td, fontSize:12, color:G.textMuted }}>{d.signedAt?`${d.signedBy} · ${new Date(d.signedAt).toLocaleDateString()}`:'—'}</td>
      </tr>
    ))}</tbody>
  </table>;
}

function EquipmentList({ equipment }) {
  if (!equipment.length) return <Empty msg="No equipment recorded yet."/>;
  return <table style={{ width:'100%', borderCollapse:'collapse' }}>
    <thead><tr>{['Type','Make/Model','Serial','Installed','Warranty','Condition'].map(h=><th key={h} style={styles.th}>{h}</th>)}</tr></thead>
    <tbody>{equipment.map(e => (
      <tr key={e.id}>
        <td style={styles.td}>{e.type}</td>
        <td style={styles.td}>{e.manufacturer} {e.model}</td>
        <td style={{ ...styles.td, fontFamily:'monospace', fontSize:11 }}>{e.serialNumber||'—'}</td>
        <td style={{ ...styles.td, fontSize:12 }}>{e.installDate||'—'}</td>
        <td style={{ ...styles.td, fontSize:12 }}>{e.warrantyExpiry||'—'}</td>
        <td style={styles.td}>{e.condition}</td>
      </tr>
    ))}</tbody>
  </table>;
}

function UnifiedCommLog({ contactId }) {
  const [comms, setComms] = useState(null);
  const [filter, setFilter] = useState('all');
  useEffect(() => {
    api(`/api/contact-detail/${contactId}/communications`).then(setComms).catch(()=>setComms([]));
  }, [contactId]);
  if (!comms) return <Spinner sz={20}/>;
  const filtered = filter === 'all' ? comms : comms.filter(c => c.channel === filter);
  if (!filtered.length) return <Empty msg="No communications recorded yet."/>;

  const channelStyle = (ch) => ({
    sms: { color: G.cyan, icon: '💬', label: 'SMS' },
    email: { color: G.purple, icon: '📧', label: 'Email' },
    call: { color: G.green, icon: '📞', label: 'Call' },
  }[ch] || { color: G.textMuted, icon: '•', label: ch });

  return <div>
    <div style={{ display:'flex', gap:6, marginBottom:14, padding:4, background:G.input, borderRadius:8, width:'fit-content' }}>
      {[['all','All'],['sms','💬 SMS'],['email','📧 Email'],['call','📞 Calls']].map(([id,l]) => (
        <Btn key={id} sz="xs" v={filter===id?'primary':'ghost'} onClick={()=>setFilter(id)}>{l}</Btn>
      ))}
    </div>
    {filtered.map(c => {
      const cs = channelStyle(c.channel);
      const isOut = c.direction === 'outbound';
      return <div key={c.id} style={{ display:'flex', gap:10, marginBottom:10, paddingBottom:10, borderBottom:`1px solid ${G.border}` }}>
        <div style={{ width:36, height:36, borderRadius:'50%', background: cs.color+'22', color: cs.color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0 }}>{cs.icon}</div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
            <Badge c={cs.color} bg={cs.color+'22'}>{cs.label}</Badge>
            <Badge c={isOut?G.purple:G.green} bg={(isOut?G.purple:G.green)+'22'}>{isOut?'OUT':'IN'}</Badge>
            {c.subject && <strong style={{ fontSize:13 }}>{c.subject}</strong>}
            <span style={{ fontSize:11, color:G.textMuted, marginLeft:'auto' }}>{new Date(c.created_at).toLocaleString()}</span>
          </div>
          {c.body && <div style={{ fontSize:13, lineHeight:1.5, color:G.text, whiteSpace:'pre-wrap', maxHeight:120, overflow:'auto' }}>{c.body}</div>}
          {c.duration_seconds != null && <div style={{ fontSize:11, color:G.textMuted, marginTop:2 }}>Duration: {Math.round(c.duration_seconds/60*10)/10} min</div>}
          {c.channel === 'call' && c.recording_url && (
            <audio controls preload="none" src={c.recording_url}
              style={{ marginTop:6, width:'100%', maxWidth:360, height:32 }}/>
          )}
          {c.channel === 'call' && c.transcript && (
            <details style={{ marginTop:6 }}>
              <summary style={{ cursor:'pointer', fontSize:12, color:G.textMuted }}>📝 Transcript</summary>
              <div style={{ marginTop:6, padding:'8px 10px', background:G.input, borderRadius:6, fontSize:12, lineHeight:1.5, whiteSpace:'pre-wrap', maxHeight:240, overflow:'auto' }}>{c.transcript}</div>
            </details>
          )}
        </div>
      </div>;
    })}
  </div>;
}

function Empty({ msg }) {
  return <div style={{ padding:40, textAlign:'center', color:G.textMuted, fontSize:13 }}>{msg}</div>;
}

function SendSmsModal({ open, contact, onClose, onSent, toast }) {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const send = async () => {
    if (!body.trim() || !contact?.phone) return;
    setBusy(true);
    try {
      await api('/api/messaging/send', { method:'POST', body:{ phone: contact.phone, body, contactId: contact.id }});
      setBody('');
      onSent();
    } catch(e) { alert(e.message); setBusy(false); }
  };
  return <Modal open onClose={onClose} width={500}>
    <h2 style={{ margin:'0 0 6px 0' }}>💬 Send SMS</h2>
    <div style={{ color:G.textMuted, fontSize:13, marginBottom:14 }}>To: <strong style={{ color:G.text }}>{contact.firstName} {contact.lastName}</strong> · {contact.phone}</div>
    <Field label="Message">
      <Textarea value={body} onChange={e=>setBody(e.target.value)} placeholder={`Hi ${contact.firstName||''}, just checking in...`} style={{ minHeight:100 }}/>
    </Field>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:14 }}>
      <span style={{ fontSize:11, color:G.textMuted }}>{body.length}/160 chars</span>
      <div style={{ display:'flex', gap:10 }}>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn v="primary" onClick={send} disabled={busy || !body.trim()}>{busy?<Spinner sz={14}/>:'Send'}</Btn>
      </div>
    </div>
  </Modal>;
}

function UploadDocModal({ open, contact, onClose, onUploaded, toast }) {
  const [title, setTitle] = useState('');
  const [type, setType] = useState('document');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  const upload = async () => {
    if (!file) return alert('Pick a file first');
    setBusy(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      const u = await api('/api/uploads', { method:'POST', body:{ name:file.name, mime:file.type, base64 }});
      // Create a document record linked to contact
      await api('/api/documents', { method:'POST', body:{
        title: title || file.name,
        type,
        contactId: contact.id,
        content: `📎 Attached file: ${u.url}\n\nFilename: ${file.name}\nUploaded: ${new Date().toLocaleString()}`,
        status: 'attached',
      }});
      onUploaded();
    } catch(e) { alert(e.message); setBusy(false); }
  };

  return <Modal open onClose={onClose} width={500}>
    <h2 style={{ margin:'0 0 6px 0' }}>📎 Attach Document</h2>
    <div style={{ color:G.textMuted, fontSize:13, marginBottom:14 }}>For <strong style={{ color:G.text }}>{contact.firstName} {contact.lastName}</strong></div>
    <Field label="Title"><Input value={title} onChange={e=>setTitle(e.target.value)} placeholder="Auto-fills from filename if blank"/></Field>
    <Field label="Type">
      <Select value={type} onChange={e=>setType(e.target.value)}>
        <option value="document">Document</option>
        <option value="agreement">Agreement</option>
        <option value="contract">Contract</option>
        <option value="inspection">Inspection</option>
        <option value="photo">Photo</option>
        <option value="receipt">Receipt</option>
        <option value="permit">Permit</option>
      </Select>
    </Field>
    <Field label="File">
      <input type="file" onChange={e=>setFile(e.target.files?.[0]||null)} style={{ ...styles.inp, padding:10 }}/>
    </Field>
    {file && <div style={{ fontSize:12, color:G.textMuted, marginTop:6 }}>{file.name} · {(file.size/1024).toFixed(1)} KB</div>}
    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
      <Btn onClick={onClose}>Cancel</Btn>
      <Btn v="primary" onClick={upload} disabled={busy || !file}>{busy?<Spinner sz={14}/>:'Upload + Attach'}</Btn>
    </div>
  </Modal>;
}

// Contact quick actions — DND toggle + remove from all workflows. Backend
// endpoints already exist (PATCH /contacts/:id/dnd + bulk unenroll_workflow).
// This surfaces them as one-click buttons on the profile header.
function ContactQuickActions({ contact, onChanged, toast }) {
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const isDnd = contact?.dnd?.sms && contact?.dnd?.email && contact?.dnd?.voice;

  const flipDnd = async (on) => {
    setBusy(true);
    try {
      await api(`/api/contacts/${contact.id}/dnd`, { method:'PATCH', body:{ channel:'all', on, reason: on ? 'Manual — profile action' : null }});
      toast?.(on ? '🚫 Contact added to DND (SMS/email/voice off)' : '✓ DND cleared', 'success');
      onChanged?.();
    } catch (e) { toast?.(e.message, 'error'); }
    setBusy(false);
  };
  const removeFromAllWorkflows = async () => {
    if (!confirm(`Remove ${contact.firstName || 'this contact'} from ALL active workflow enrollments? This pauses every drip / nurture / follow-up. Can be re-enrolled later.`)) return;
    setBusy(true);
    try {
      // Reuse the bulk unenroll endpoint (fires one row per active workflow).
      const wfs = await api('/api/workflows').catch(()=>({workflows:[]}));
      const list = wfs.workflows || wfs || [];
      let paused = 0;
      for (const w of list) {
        try { const r = await api('/api/contacts/bulk', { method:'POST', body:{ action:'unenroll_workflow', ids:[contact.id], value: w.id }}); paused += r.affected || 0; } catch {}
      }
      toast?.(`✓ Removed from ${paused} workflow enrollment${paused===1?'':'s'}`, 'success');
      onChanged?.();
    } catch (e) { toast?.(e.message, 'error'); }
    setBusy(false);
    setOpen(false);
  };

  return <div style={{ position:'relative', display:'inline-block' }}>
    <Btn sz="sm" onClick={()=>setOpen(o=>!o)} style={{ background: isDnd ? G.red+'22' : undefined, color: isDnd ? G.red : undefined, borderColor: isDnd ? G.red+'66' : undefined }}>
      {isDnd ? '🚫 DND' : '⋯ Actions'}
    </Btn>
    {open && <div style={{ position:'absolute', right:0, top:'100%', marginTop:6, minWidth:250, background:G.card, border:`1px solid ${G.border}`, borderRadius:10, padding:6, boxShadow:'0 8px 24px rgba(0,0,0,.4)', zIndex:20 }}>
      <button onClick={()=>flipDnd(!isDnd)} disabled={busy}
        style={{ display:'block', width:'100%', textAlign:'left', padding:'10px 12px', background:'transparent', border:'none', color:G.text, borderRadius:6, cursor:'pointer', fontSize:13 }}
        onMouseEnter={e=>e.currentTarget.style.background = G.input}
        onMouseLeave={e=>e.currentTarget.style.background = 'transparent'}>
        {isDnd ? '✓ Clear Do-Not-Disturb' : '🚫 Add to DND (all outbound)'}
        <div style={{ fontSize:11, color:G.textMuted, marginTop:2 }}>{isDnd ? 'Resume SMS / email / voice outreach' : 'Block SMS + email + voice from this contact'}</div>
      </button>
      <button onClick={removeFromAllWorkflows} disabled={busy}
        style={{ display:'block', width:'100%', textAlign:'left', padding:'10px 12px', background:'transparent', border:'none', color:G.red, borderRadius:6, cursor:'pointer', fontSize:13 }}
        onMouseEnter={e=>e.currentTarget.style.background = G.red+'11'}
        onMouseLeave={e=>e.currentTarget.style.background = 'transparent'}>
        ❌ Remove from all workflows
        <div style={{ fontSize:11, color:G.textMuted, marginTop:2 }}>Pause every active drip / nurture / follow-up</div>
      </button>
    </div>}
  </div>;
}
